import { Component, OnInit, Inject, HostBinding, ViewEncapsulation } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';

import { UntypedFormBuilder, UntypedFormControl, Validators } from '@angular/forms';

import * as _ from 'lodash';
import { AttributesPopupComponent } from '../attributes-popup.component';
import { CalibrationSetAttributeService } from 'src/app/core/services/calibration-set-attribute.service';
import { LinksValidator } from '../../../../../shared/zoneiqui/components/links/links.validator';
import { emptyStringValidator } from '../../../../../shared/zoneiqui/components/links/empty-string.validator';
import { ICalibrationSetAttributeModel } from 'src/app/interfaces/calibration-set-attribute-model.interface';
import { CalibrationSetAttribute } from 'src/app/models/calibrations/calibration-set-attribute.model';
import { AttributeType } from 'src/shared/enums/attribute-type.enum';
import { ZoningCodeService } from 'src/app/core/services/zoning-code.service';
import { IZoningCodeModel } from 'src/app/interfaces/zoning-code-model.interface';
import { IAttributePopupParams } from 'src/app/interfaces/attribute-popup-params.interface';
import { IZoneCombinationModel } from 'src/app/interfaces/zone-combination-model.interface';
import { ZoneCombinationService } from 'src/app/core/services';
import { zoneCombinationMapperFactory } from 'src/app/mappers/zone-combination.mapper';
import { CalibrationSetType } from 'src/shared/enums/calibration-set-type';
import { IOverlayModel } from 'src/app/interfaces/overlay-model.interface';
import { ICalibrationSetModel } from 'src/app/interfaces/calibration-set-model.interface';
import { OverlayService } from 'src/app/core/services/overlay.service';
import { overlayMapperFactory } from 'src/app/mappers/overlay.mapper';

@Component({
    standalone: false,
    selector: 'calibration-set-attributes-popup',
    templateUrl: './calibration-set-attributes-popup.component.html',
    styleUrls: ['../attributes-popup.component.scss', './calibration-set-attributes-popup.component.scss'],
    encapsulation: ViewEncapsulation.None

})
export class CalibrationSetAttributesPopupComponent extends AttributesPopupComponent implements OnInit {
    @HostBinding('class')
    readonly hostClass = 'attributes-popup';
    zoningCode: IZoningCodeModel;
    zoneCombination: IZoneCombinationModel;
    overlay: IOverlayModel;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: IAttributePopupParams,
        public attributeService: CalibrationSetAttributeService,
        protected readonly formBuilder: UntypedFormBuilder,
        public dialogRef: MatDialogRef<CalibrationSetAttributesPopupComponent>,
        private zoningCodeService: ZoningCodeService,
        protected zoneCombinationService: ZoneCombinationService,
        protected overlayService: OverlayService
    ) {
        super(data, formBuilder);
        this.zoningCode = this.zoningCodeService.selectedZoningCode;
        switch (data.parameters.ParentType) {
            case CalibrationSetType.ZONE_COMBINATION:
                this.zoneCombination = this.zoningCode.getZoneCombinationById(data.parameters.ParentId);
                break;
            case CalibrationSetType.OVERLAY:
            case CalibrationSetType.TYPOLOGY:
                this.overlay = this.zoningCode.getOverlayById(data.parameters.ParentId);
                break;
        }
    }

    get parentId() {
        return _.get(this.data, 'parameters.ParentId', 0);
    }

    get parentType() {
        return _.get(this.data, 'parameters.ParentType', 0);
    }

    get assumption(): ICalibrationSetAttributeModel {
        return super.assumption as ICalibrationSetAttributeModel;
    }

    get calibrationSet(): ICalibrationSetModel<any> {
        return this.zoneCombination || this.overlay;
    }

    get canSave(): boolean {
        return super.canSave && this.zoneCombination && this.zoneCombination.canSaveIsNotCalibrated;
    }

    ngOnInit() {
        this.linkForm = this.formBuilder.group({
            linkValue: new UntypedFormControl('', [LinksValidator.linkValidator, Validators.required]),
            linkDescription: new UntypedFormControl('', [
                emptyStringValidator('linkDescription'),
                Validators.maxLength(250),
                Validators.required
            ])
        });
        this.dialogReference = this.dialogRef;
        this.attributeService
            .getAttributesForCalibrationSet(this.parentId, this.parentType)
            .subscribe(attributes => this.initAttributes(attributes));
    }

    addNewLink() {
        if (this.linkForm.invalid) {
            return;
        }

        const newLink = new CalibrationSetAttribute({
            ParentId: this.parentId,
            ParentType: this.parentType,
            AttributeType: AttributeType.LINK,
            AttributeValue: this.linkForm.value.linkValue,
            Description: this.linkForm.value.linkDescription
        });
        super.addNewLink(newLink);
    }

    async save() {
        await super.save();

        const queries = this.getAttributeOperationsObservables();

        if (_.get(this, 'zoneCombination.isChanged')) {
            const mapper = zoneCombinationMapperFactory();
            this.zoneCombination.acceptChanges();
            queries.push(this.zoneCombinationService.saveChanges([mapper.toDTO(this.zoneCombination.currentState)]));
            queries.push(this.zoneCombinationService.replaceZoneCombinationINCRs(this.zoneCombination.currentState));
        }

        if (_.get(this, 'overlay.isChanged')) {
            const mapper = overlayMapperFactory();
            this.overlay.acceptChanges();
            queries.push(this.overlayService.updateOverlay(mapper.toDTO(this.overlay.currentState)));
        }

        this.callForkJoin(queries);
        this.dialogReference.close();
    }

    initDefaultAssumption() {
        if (!this.assumptionsValue.length) {
            this.setAssumptions(
                this.assumptionsValue.concat(
                new CalibrationSetAttribute({
                    ParentId: this.parentId,
                    ParentType: this.parentType,
                    AttributeType: AttributeType.ASSUMPTION,
                    AttributeValue: '',
                    IsEnabled: false
                })
                )
            );
        }
    }

    initDefaultComment() {
        if (!this.commentsValue.length) {
            this.setComments(
                this.commentsValue.concat(
                new CalibrationSetAttribute({
                    ParentId: this.parentId,
                    ParentType: this.parentType,
                    AttributeType: AttributeType.COMMENT,
                    AttributeValue: ''
                })
                )
            );
        }
    }

    onCancel() {
        if (this.calibrationSet) {
            this.calibrationSet.cancelChanges();
        }
    }
}
